import { T, useTranslate } from '@tolgee/react';
import { EmptyState } from '~/components/empty/EmptyState';
import { Button } from '~/components/ui/Button';
import { WrapperWidget } from '@agentselly/ui';
import { useState } from 'react';
import { Link as LinkIcon } from '~/generated/svg/icon/Link';

interface NearObject {
  readonly propertyId: string;
  readonly propertyType: string;
  readonly address: string;
  readonly distance: number;
}

interface NearObjectItemProps {
  readonly object: NearObject;
}

function NearObjectItem({ object }: NearObjectItemProps) {
  return (
    <li className="p-3 hover:bg-gray-100 dark:hover:bg-zinc-800 transition-colors rounded-lg">
      <a
        className="flex w-full"
        href={`https://www.agentselly.ch/marktplatz/${object.propertyId}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        <div className="flex-grow pr-2">
          <span className="text-sm break-words text-gray-700 dark:text-gray-300">
            <T keyName={`home_${object.propertyType}`} /> in {object.address}
          </span>
        </div>
        <div className="flex items-center flex-shrink-0 gap-2">
          <span className="px-1.5 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 min-w-[3rem] text-center whitespace-nowrap">
            {object.distance < 1
              ? `${Math.round(object.distance * 1000)}m`
              : `${object.distance.toFixed(1)}km`}
          </span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="-0.375 -0.375 18 18"
            height="18"
            width="18"
            className="text-gray-500 dark:text-zinc-400"
          >
            <desc>Arrow Right 1 Streamline Icon: https://streamlinehq.com</desc>
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              d="m4.0616562499999995 16.890625 9.039 -7.9968125a0.359375 0.359375 0 0 0 0 -0.537625L4.0616562499999995 0.359375"
              strokeWidth="0.75"
            ></path>
          </svg>
        </div>
      </a>
    </li>
  );
}

interface Props {
  readonly soldNearObjects: NearObject[];
  readonly onlineNearObjects: NearObject[];
  readonly showAllObjects?: boolean;
  readonly searchZip?: number;
  readonly isProd?: boolean;
}

export function NearObjectsWidget({
  soldNearObjects,
  onlineNearObjects,
  showAllObjects = true,
  searchZip,
  isProd = true,
}: Props) {
  const { t } = useTranslate();
  const [
    displayedOnline,
    setDisplayedOnline,
  ] = useState(10);
  const [
    displayedSold,
    setDisplayedSold,
  ] = useState(10);

  const marketPlaceUrl = `https://www.agentselly.${
    isProd ? 'ch' : 'dev'
  }/marktplatz`;

  if (!soldNearObjects?.length && !onlineNearObjects?.length) {
    return (
      <WrapperWidget>
        <div className="h-full flex flex-col gap-y-6">
          <EmptyState
            title={t('widget_near_objects_title')}
            description={t('widget_near_objects_description')}
          />
        </div>
      </WrapperWidget>
    );
  }

  const handleLoadMoreOnline = () => setDisplayedOnline(displayedOnline + 10);
  const handleLoadMoreSold = () => setDisplayedSold(displayedSold + 10);

  return (
    <WrapperWidget>
      <div className="h-full flex flex-col gap-y-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          {t('widget_near_objects_title')}
        </h2>
        <p className="text-sm text-gray-600 dark:text-zinc-400">
          {t('widget_near_objects_description')}
        </p>
        <div className="flex flex-col md:flex-row">
          {onlineNearObjects.length > 0 && (
            <div className="flex-1">
              <a
                href={`${marketPlaceUrl}?soldFilter=unsold&radius=20&location=${
                  searchZip || ''
                }`}
                className="font-bold text-md my-2 hover:text-primary-400 flex items-center gap-2 text-gray-900 dark:text-gray-100"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="flex items-center gap-2">
                  {t('widget_near_objects_online_title', {
                    value: onlineNearObjects.length,
                  })}
                </span>
                <LinkIcon className="h-2 w-2 inline-block align-middle" />
              </a>
              <ul className="divide-y divide-gray-200 dark:divide-zinc-700">
                {onlineNearObjects
                  .slice(0, showAllObjects ? displayedOnline : 1)
                  .map((object, idx) => (
                    <NearObjectItem
                      key={idx}
                      object={object}
                    />
                  ))}
              </ul>
              {showAllObjects && displayedOnline < onlineNearObjects.length && (
                <LoadMoreButton handleLoadMore={handleLoadMoreOnline} />
              )}
            </div>
          )}

          {soldNearObjects?.length > 0 && (
            <div className="flex-1 md:ml-4">
              <a
                href={`${marketPlaceUrl}?soldFilter=sold&radius=20&location=${
                  searchZip || ''
                }`}
                className="font-bold text-md my-2 hover:text-primary-400 flex items-center gap-2 text-gray-900 dark:text-gray-100"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="flex items-center gap-2">
                  {t('widget_near_objects_sold_title', {
                    value: soldNearObjects.length,
                  })}
                </span>
                <LinkIcon className="h-2 w-2 inline-block align-middle" />
              </a>
              <ul className="divide-y divide-gray-200 dark:divide-zinc-700">
                {soldNearObjects
                  .slice(0, showAllObjects ? displayedSold : 1)
                  .map((object, idx) => (
                    <NearObjectItem
                      key={idx}
                      object={object}
                    />
                  ))}
              </ul>
              {showAllObjects && displayedSold < soldNearObjects.length && (
                <LoadMoreButton handleLoadMore={handleLoadMoreSold} />
              )}
            </div>
          )}
        </div>
        <div className="self-start">
          <a
            href={marketPlaceUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button showArrow>{t('widget_near_objects_button')}</Button>
          </a>
        </div>
      </div>
    </WrapperWidget>
  );
}

function LoadMoreButton(props: { handleLoadMore: () => void }) {
  return (
    <button
      onClick={props.handleLoadMore}
      className="md:px-8 py-4 text-sm flex items-center bg-gray-100 dark:bg-zinc-800 hover:bg-gray-200 dark:hover:bg-zinc-700 rounded p-2 w-full gap-2 text-gray-700 dark:text-gray-300"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="-0.335 -0.335 16 16"
        height="16"
        width="16"
        id="More-Add-Plus--Streamline-Platinum"
        className="text-gray-600 dark:text-zinc-400"
      >
        <desc>More Add Plus Streamline Icon: https://streamlinehq.com</desc>
        <g id="more-add-plus">
          <path
            id="Vector"
            stroke="currentColor"
            d="M7.664999999999999 0.6387499999999999v14.052499999999998m7.026249999999999 -7.026249999999999L0.6387499999999999 7.664999999999999"
            strokeWidth="0.67"
          ></path>
        </g>
      </svg>
      <T>widget_near_objects_load_more_button</T>
    </button>
  );
}
